import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { crm, users } from '../../services/api';

/*
 * Contacto por SMS ao balcão. O servidor sabe enviar SMS desde o módulo de mensagens
 * (POST /api/crm/members/{userId}/sms, Admin, via MagSend) — o que faltava era o ecrã: o botão «SMS»
 * da Operação/Receção aparecia desativado com a razão «o envio de SMS não está implementado no
 * servidor», o que era falso.
 *
 * Vai para o número que está no registo do membro (não se escreve um número à mão: o SMS fica ligado
 * ao membro e apareceu no histórico dele). O servidor distingue três falhas que pedem coisas
 * diferentes a quem está ao balcão e este diálogo diz cada uma por palavras suas — nunca diz
 * «enviado» quando não foi.
 *
 * `member` + `initialMessage` abrem o mesmo diálogo já com o sócio escolhido e a frase escrita (é o
 * caminho do painel de sócios inativos). Sem eles, o diálogo é o do balcão, com pesquisa.
 */
export default function SmsDialog({ open, onClose, member = null, initialMessage = '' }) {
  const [all, setAll] = useState([]);
  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setTerm(member?.name || ''); setSelected(member || null); setMessage(initialMessage || '');
    setProblem(null); setLoadError(false);
    users.getAll()
      .then((r) => { if (alive) setAll(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setLoadError(true); });
    return () => { alive = false; };
  }, [open, member, initialMessage]);

  const matches = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (q.length < 2) return [];
    return all
      .filter((u) => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [term, all]);

  if (!open) return null;

  const phone = selected?.phone || '';
  const segments = message.trim() ? Math.ceil(message.trim().length / 160) : 0;

  const send = async () => {
    if (!selected) { toast.warning('Escolha o membro que vai receber o SMS'); return; }
    if (!phone) { setProblem({ kind: 'no-phone', text: 'Este membro não tem telefone no registo. Atualize a ficha primeiro.' }); return; }
    if (message.trim().length < 2) { toast.warning('Escreva a mensagem'); return; }
    setBusy(true); setProblem(null);
    try {
      const res = await crm.memberSms(selected.id, message.trim());
      if (res.data?.sent) {
        toast.success('SMS enviado');
        onClose();
      } else {
        setProblem({ kind: 'failed', text: `O gateway não confirmou o envio${res.data?.status ? ` (${res.data.status})` : ''}.` });
      }
    } catch (e) {
      const d = e.response?.data || {};
      const status = e.response?.status;
      if (d.error === 'feature-disabled' || (status === 403 && d.feature)) {
        // O SMS ao balcão passa pelo controlador do CRM: o servidor diz QUAL módulo está desligado e o ecrã di-lo,
        // em vez de acusar o SMS por uma coisa que é do CRM.
        const text = d.feature === 'LeadsCrm'
          ? 'O envio de SMS ao balcão passa pelo módulo Leads/CRM, que está desligado neste ginásio. Ligue-o em Módulos para poder enviar.'
          : d.feature && d.feature !== 'Sms'
            ? `O módulo ${d.feature} está desligado neste ginásio.`
            : 'O módulo de SMS está desligado neste ginásio.';
        setProblem({ kind: 'disabled', text });
      } else if (d.error === 'no-phone') {
        setProblem({ kind: 'no-phone', text: 'Este membro não tem telefone no registo.' });
      } else if (d.error === 'empty-message') {
        setProblem({ kind: 'failed', text: 'A mensagem ficou vazia.' });
      } else if (status === 404) {
        setProblem({ kind: 'failed', text: 'O membro já não existe nesta base.' });
      } else {
        setProblem({ kind: 'failed', text: d.error || d.message || 'Não foi possível enviar o SMS.' });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={S.backdrop} role="dialog" aria-modal="true" aria-label="Enviar SMS">
      <div style={S.panel}>
        <div style={S.head}>
          <h3 style={S.title}>Enviar SMS</h3>
          <button type="button" style={S.close} onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <label style={S.label} htmlFor="sms-search">Membro</label>
        <input id="sms-search" style={S.input} value={term} onChange={(e) => { setTerm(e.target.value); setSelected(null); }}
               placeholder="Nome ou email (mínimo 2 letras)" autoFocus />
        {loadError && <div style={S.error}>Não foi possível carregar a lista de membros.</div>}
        {!loadError && term.trim().length >= 2 && matches.length === 0 && <div style={S.hint}>Sem resultados.</div>}
        {matches.length > 0 && (
          <ul style={S.list}>
            {matches.map((u) => (
              <li key={u.id}>
                <button type="button" style={{ ...S.option, ...(selected?.id === u.id ? S.optionActive : {}) }} onClick={() => { setSelected(u); setProblem(null); }}>
                  <span style={S.optionName}>{u.name || '(sem nome)'}</span>
                  <span style={S.optionMeta}>{u.phone ? `📱 ${u.phone}` : 'sem telefone no registo'}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <label style={S.label} htmlFor="sms-msg">Mensagem</label>
        <textarea id="sms-msg" style={S.textarea} rows={4} value={message} onChange={(e) => setMessage(e.target.value)} />
        <div style={S.hint}>
          {segments > 0 ? `${segments} SMS (160 caracteres cada). ` : ''}
          Vai para o número do registo{phone ? `: ${phone}` : ''}.
        </div>

        {problem && (
          <div style={problem.kind === 'failed' ? S.error2 : S.warn}>
            <strong>
              {problem.kind === 'disabled' ? 'SMS desligado' : problem.kind === 'no-phone' ? 'Sem número' : 'Não enviado'}
            </strong>
            <div style={S.reason}>{problem.text}</div>
            {problem.kind === 'disabled' && <div style={S.reason}>Definições → Módulos → SMS.</div>}
          </div>
        )}

        <div style={S.footer}>
          <button type="button" style={S.secondary} onClick={onClose}>Cancelar</button>
          <button type="button" style={S.primary} disabled={busy} onClick={send}>{busy ? 'A enviar…' : 'Enviar'}</button>
        </div>
      </div>
    </div>
  );
}

const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 },
  panel: { background: 'var(--bg-card)', color: 'var(--text-primary)', borderRadius: 'var(--radius-lg, 14px)', padding: 20, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg, 0 20px 40px rgba(0,0,0,.2))' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  title: { margin: 0, fontSize: 18, fontWeight: 700 },
  close: { border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--text-secondary)' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, margin: '12px 0 6px', color: 'var(--text-secondary)' },
  input: { width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-input, var(--bg-card))', color: 'var(--text-primary)', fontSize: 14 },
  textarea: { width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-input, var(--bg-card))', color: 'var(--text-primary)', fontSize: 14 },
  list: { listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  option: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-card)', cursor: 'pointer' },
  optionActive: { borderColor: 'var(--accent-primary)', background: 'var(--bg-hover)' },
  optionName: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' },
  optionMeta: { fontSize: 12, color: 'var(--text-secondary)' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  primary: { padding: '10px 14px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--accent-primary)', color: 'var(--text-inverse)', fontSize: 14, cursor: 'pointer' },
  secondary: { padding: '10px 14px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer' },
  hint: { fontSize: 13, color: 'var(--text-muted)', marginTop: 8 },
  error: { fontSize: 13, color: 'var(--error-text)', marginTop: 8 },
  error2: { border: '1px solid var(--error)', background: 'var(--error-bg)', color: 'var(--error-text)', borderRadius: 'var(--radius-md, 8px)', padding: 12, fontSize: 14, marginTop: 12 },
  warn: { border: '1px solid var(--warning)', background: 'var(--warning-bg)', color: 'var(--warning-text)', borderRadius: 'var(--radius-md, 8px)', padding: 12, fontSize: 14, marginTop: 12 },
  reason: { fontSize: 13, marginTop: 4 },
};
